import { useEffect, useState } from "react";
import { Outlet, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import {
  clearTokens,
  getAccessToken,
  getRefreshToken,
  refreshRequest,
  setTokens,
} from "@/lib/auth";
import { api } from "@/lib/api";
import {
  clearStoredEnvironmentState,
  getStoredCurrentEnvironment,
  type AdminEnvironment,
} from "@/lib/environment";

/**
 * 路由守卫：
 * - 必须具备 access token，否则立即跳 /login
 * - 有 token 但 admin/env 尚未就绪时（跨域环境切换 handoff、首次同步中）显示加载态
 * - mount 时调一次 /auth/me 与 backend 校准 admin / env_options / current_env，
 *   兼容跨域 handoff（token 存在但 admin/env 在新 origin 的 localStorage 中为空）
 * - access token 校验失败由 axios 响应拦截器（401 → refresh → fail → redirect）兜底
 */
export function AuthGuard() {
  // ── 吸收跨域环境切换的 token handoff（同步，在任何 guard 检查之前执行）────
  // 跨域跳转时 EnvironmentSwitcher 将 _at/_rt 追加到 URL，此处一次性读取并写入
  // 当前 origin 的 localStorage，随后立即清除 URL 参数避免留在浏览器历史。
  useState(() => {
    const params = new URLSearchParams(window.location.search);
    const at = params.get("_at");
    if (!at) return undefined;
    const rt = params.get("_rt");
    setTokens({ access_token: at, refresh_token: rt ?? "", expires_in: 86400, user_role: "" });
    params.delete("_at");
    params.delete("_rt");
    const q = params.toString();
    window.history.replaceState(null, "", q ? `?${q}` : window.location.pathname);
    return undefined;
  });

  const { admin, currentEnv, setEnvironmentState, login } = useAdminAuth();
  const location = useLocation();
  const hasToken = Boolean(getAccessToken());
  const [synced, setSynced] = useState(false);
  const [authFailed, setAuthFailed] = useState(false);

  useEffect(() => {
    // token 不存在：guard 同步检查会跳 /login，无需调 /auth/me
    if (!hasToken) {
      setSynced(true);
      return;
    }
    // 有 token 时统一调 /auth/me：
    //   - 正常情况：校准 env_options / current_env
    //   - handoff 情况：token 刚被吸收，admin/env 在新 origin 为空，/auth/me 负责还原
    let cancelled = false;
    const clearAuthAndRedirect = () => {
      clearTokens();
      localStorage.removeItem("quantum_admin");
      clearStoredEnvironmentState();
      if (!cancelled) setAuthFailed(true);
    };
    const syncAuthMe = async () => {
      const res = await api.get("/quantum/business/auth/me");
      if (cancelled) return;
      const env_options: AdminEnvironment[] = res.data?.data?.env_options ?? [];
      const backend_current: AdminEnvironment | null =
        res.data?.data?.current_env ?? null;

      // 域名匹配优先级最高：admin_base_url.hostname === 当前页面 hostname
      const domainCurrent =
        env_options.find((o) => {
          try {
            return (
              o.admin_base_url &&
              new URL(o.admin_base_url).hostname === window.location.hostname
            );
          } catch {
            return false;
          }
        }) ?? null;

      // reconcile 优先级：域名匹配 > localStorage（若仍有效）> backend 默认值
      const localCurrent = getStoredCurrentEnvironment();
      const stillValid =
        localCurrent && env_options.some((o) => o.id === localCurrent.id);
      const nextCurrent =
        domainCurrent ?? (stillValid ? localCurrent : null) ?? backend_current;

      if (!admin && res.data?.data?.user) {
        // handoff 场景：admin 在新 origin 为空，用 login 同时还原 admin + env 状态
        login(res.data.data.user as Parameters<typeof login>[0], env_options, nextCurrent);
      } else {
        setEnvironmentState(env_options, nextCurrent);
      }
    };
    (async () => {
      try {
        await syncAuthMe();
      } catch (err) {
        const status = axios.isAxiosError(err) ? err.response?.status : undefined;
        if (status === 401) {
          if (!getRefreshToken()) {
            clearAuthAndRedirect();
            return;
          }
          try {
            const newTokens = await refreshRequest();
            setTokens(newTokens);
            await syncAuthMe();
          } catch {
            clearAuthAndRedirect();
          }
        }
        // 其他错误保留本地兜底；如果本地没有 admin/env，下面会继续显示恢复态。
      } finally {
        if (!cancelled) setSynced(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // mount-only sync：setEnvironmentState / login 后状态会变，避免重复触发
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 无 token → 立即跳登录
  if (authFailed || !hasToken) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // 有 token 但 admin/env 尚未就绪（handoff 还原中 / 首次同步中）→ 等待
  if (!admin || !currentEnv || !synced) {
    return (
      <div className="min-h-screen flex items-center justify-center text-text-muted text-sm">
        正在恢复登录状态…
      </div>
    );
  }

  return <Outlet />;
}
